import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Flame, Activity, Scale, Ruler, ChevronDown } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';
import { CalculatorJsonLd } from '../components/seo/SchemaMarkup';
import { ToolSEOWrapper } from '../components/tools/ToolSEOWrapper';
import { EmailResultsGate } from '../components/tools/EmailResultsGate';

const TDEE_SEO = {
  name: "TDEE Calculator - Total Daily Energy Expenditure",
  description: "Calculate your Total Daily Energy Expenditure (TDEE) using the Mifflin-St Jeor equation. Find your maintenance, cutting, and bulking calories.",
  url: "https://ninjathlete.com/tools/tdee-calculator",
  steps: [
    { name: "Select your sex", text: "Choose Male or Female for accurate BMR calculation." },
    { name: "Enter your age", text: "Input your age in years." },
    { name: "Enter height and weight", text: "Provide your height (feet/inches or cm) and weight (lbs or kg)." },
    { name: "Select activity level", text: "Choose from Sedentary to Extremely Active based on your weekly exercise." },
    { name: "Calculate TDEE", text: "Click Calculate to see your BMR, maintenance calories, and recommended targets for cutting and bulking." },
  ],
  faqs: [
    { question: "What is TDEE?", answer: "TDEE stands for Total Daily Energy Expenditure. It's the total number of calories you burn per day, including exercise and daily activities." },
    { question: "How do I calculate my BMR?", answer: "We use the Mifflin-St Jeor equation: For men, BMR = 10 x weight(kg) + 6.25 x height(cm) - 5 x age - 5. For women, BMR = 10 x weight(kg) + 6.25 x height(cm) - 5 x age - 161." },
    { question: "How many calories should I eat to lose weight?", answer: "A moderate deficit of 300-500 calories below your TDEE promotes steady fat loss of about 0.5-1 lb per week while preserving muscle mass." },
  ],
};

const ACTIVITY_MULTIPLIERS = [
  { value: 'sedentary', label: 'Sedentary (office job, little exercise)', factor: 1.2 },
  { value: 'light', label: 'Lightly Active (1-3 days/week)', factor: 1.375 },
  { value: 'moderate', label: 'Moderately Active (3-5 days/week)', factor: 1.55 },
  { value: 'active', label: 'Very Active (6-7 days/week)', factor: 1.725 },
  { value: 'extreme', label: 'Extremely Active (athlete/physical job)', factor: 1.9 },
];

const Select = ({ label, value, options, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  return (
    <div className="relative" data-testid={testId}>
      <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">{label}</label>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm hover:border-[#E63946]/50 transition-colors">
        <span>{selected?.label || 'Select...'}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 w-full mt-1 bg-zinc-800 border border-zinc-700 shadow-xl max-h-60 overflow-auto">
          {options.map(o => (
            <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-4 py-3 text-sm ${o.value === value ? 'bg-[#E63946]/20 text-white' : 'text-zinc-300 hover:bg-zinc-700'}`}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function TDEECalculatorPage() {
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [unit, setUnit] = useState('imperial');
  const [activity, setActivity] = useState('moderate');
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const { saveResult } = useResults();

  const calculate = () => {
    if (!age || !weight || !height) return;
    let weightKg, heightCm;
    if (unit === 'imperial') {
      weightKg = parseFloat(weight) * 0.453592;
      heightCm = parseFloat(height) * 2.54;
    } else {
      weightKg = parseFloat(weight);
      heightCm = parseFloat(height);
    }
    // Mifflin-St Jeor
    let bmr;
    if (gender === 'male') {
      bmr = 10 * weightKg + 6.25 * heightCm - 5 * parseFloat(age) + 5;
    } else {
      bmr = 10 * weightKg + 6.25 * heightCm - 5 * parseFloat(age) - 161;
    }
    const actData = ACTIVITY_MULTIPLIERS.find(a => a.value === activity);
    const tdee = Math.round(bmr * actData.factor);
    setResult({
      bmr: Math.round(bmr),
      tdee,
      cut: Math.round(tdee - 500),
      mild_cut: Math.round(tdee - 250),
      bulk: Math.round(tdee + 300),
      aggressive_bulk: Math.round(tdee + 500),
    });
    setSaved(false);
  };

  const InputField = ({ label, value, onChange, placeholder, icon: Icon, testId }) => (
    <div data-testid={testId}>
      <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#E63946]" />}
        <input type="number" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors`} />
      </div>
    </div>
  );

  return (
    <ToolSEOWrapper toolKey="tdee-calculator">
    <main className="pt-20 min-h-screen" data-testid="tdee-calculator-page">
      <CalculatorJsonLd {...TDEE_SEO} calculatorName={TDEE_SEO.name} />
      <section className="container-ninja section-spacing">
        <Link to="/tools" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /><span className="text-sm uppercase tracking-wider font-heading">Back to Tools</span>
        </Link>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-[#E63946] text-white mb-4">Calculator</span>
            <h1 className="text-4xl md:text-5xl text-white font-heading mb-3" data-testid="tdee-calc-title">TDEE & Calorie Calculator</h1>
            <p className="text-zinc-400">Find your Total Daily Energy Expenditure using the Mifflin-St Jeor equation</p>
          </div>

          <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8">
            {/* Gender Toggle */}
            <div className="mb-4">
              <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Gender</label>
              <div className="flex gap-2" data-testid="gender-toggle">
                {['male', 'female'].map(g => (
                  <button key={g} onClick={() => setGender(g)}
                    className={`flex-1 py-3 text-sm font-heading uppercase tracking-wider transition-colors ${
                      gender === g ? 'bg-[#E63946] text-white' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:text-white'}`}>
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <InputField label="Age" value={age} onChange={setAge} placeholder="Years" testId="age-input" />
              <Select label="Unit System" value={unit} options={[
                { value: 'imperial', label: 'Imperial (lbs/inches)' },
                { value: 'metric', label: 'Metric (kg/cm)' }
              ]} onChange={setUnit} testId="unit-system-select" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <InputField label={unit === 'imperial' ? 'Weight (lbs)' : 'Weight (kg)'} value={weight} onChange={setWeight} placeholder="Enter weight" icon={Scale} testId="weight-input" />
              <InputField label={unit === 'imperial' ? 'Height (inches)' : 'Height (cm)'} value={height} onChange={setHeight} placeholder="Enter height" icon={Ruler} testId="height-input" />
            </div>
            <div className="mb-6">
              <Select label="Activity Level" value={activity} options={ACTIVITY_MULTIPLIERS} onChange={setActivity} testId="activity-select" />
            </div>
            <button onClick={calculate} disabled={!age || !weight || !height}
              className="w-full py-4 bg-gradient-to-r from-[#E63946] to-[#ff6b35] text-white font-heading text-lg uppercase tracking-wider hover:from-[#d62f3c] hover:to-[#e5612e] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="calculate-tdee-btn">
              Calculate My TDEE
            </button>
          </div>

          {result && (
            <div className="mt-8" data-testid="tdee-results">
              <EmailResultsGate
                toolId="tdee-calculator"
                toolName="TDEE"
                resultPayload={{
                  bmr: result.bmr,
                  tdee: result.tdee,
                  cut: result.cut,
                  bulk: result.bulk,
                  gender,
                  age: parseInt(age),
                  weight: parseFloat(weight),
                  unit,
                }}
              >
                <div className="space-y-6 animate-entrance">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-zinc-900/80 border border-zinc-800 p-6 text-center">
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Basal Metabolic Rate</p>
                      <p className="text-4xl font-heading text-zinc-300">{result.bmr}</p>
                      <p className="text-zinc-600 text-xs">calories/day at rest</p>
                    </div>
                    <div className="bg-zinc-900/80 border border-[#E63946]/30 p-6 text-center">
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Maintenance TDEE</p>
                      <p className="text-5xl font-heading text-white" data-testid="tdee-value">{result.tdee}</p>
                      <p className="text-zinc-600 text-xs">calories/day to maintain weight</p>
                    </div>
                  </div>
                  <div className="bg-zinc-900/80 border border-zinc-800 p-6">
                    <h3 className="text-white text-lg font-heading mb-4 flex items-center gap-2">
                      <Flame className="w-5 h-5 text-[#E63946]" /> Calorie Targets by Goal
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: 'Aggressive Cut', val: result.cut, desc: '-500 cal', color: '#E63946' },
                        { label: 'Mild Cut', val: result.mild_cut, desc: '-250 cal', color: '#ff6b35' },
                        { label: 'Lean Bulk', val: result.bulk, desc: '+300 cal', color: '#00F0FF' },
                        { label: 'Bulk', val: result.aggressive_bulk, desc: '+500 cal', color: '#22c55e' },
                      ].map(t => (
                        <div key={t.label} className="p-4 bg-zinc-800/50 border border-zinc-700/50 text-center">
                          <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">{t.label}</p>
                          <p className="text-2xl font-heading" style={{ color: t.color }}>{t.val}</p>
                          <p className="text-zinc-600 text-xs">{t.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <SaveResultButton
                    saved={saved}
                    onSave={() => {
                      saveResult('tdee', {
                        value: result.tdee,
                        label: `${result.tdee} cal/day TDEE`,
                        bmr: result.bmr, tdee: result.tdee,
                        cut: result.cut, bulk: result.bulk,
                        gender, age: parseInt(age), weight: parseFloat(weight), unit,
                      });
                      setSaved(true);
                    }}
                  />
                </div>
              </EmailResultsGate>
            </div>
          )}
        </div>
      </section>
    </main>
    </ToolSEOWrapper>
  );
}
